import {Injectable, Injector, Optional} from '@angular/core';
import {BusinessEvent, BusinessEventActor, BusinessEventEntity, BusinessEventError, BusinessEventStatus, BusinessEventType} from "../models/business-event";
import {AngularFirestore, AngularFirestoreCollection} from "@angular/fire/compat/firestore";
import {AngularFireAuth} from "@angular/fire/compat/auth";
import {AuthService} from "../../auth/services/auth.service";
import {firstValueFrom, Observable} from "rxjs";
import {Timestamp} from "firebase/firestore";
import {map, take} from "rxjs/operators";
import firebase from "firebase/compat";
import Query = firebase.firestore.Query;

/**
 * Service for logging business events to Firestore
 * This service provides a one-stop shop for debugging when customers and users encounter issues
 * Note: This service does NOT extend AbstractDatabaseService to avoid circular dependencies
 */
@Injectable({
  providedIn: 'root'
})
export class BusinessEventService {
  private collection: AngularFirestoreCollection<BusinessEvent>;
  private injector?: Injector;

  constructor(private afs: AngularFirestore,
              private afAuth: AngularFireAuth,
              injector: Injector) {
    this.collection = this.afs.collection<BusinessEvent>('events');
    this.injector = injector;
  }

  /**
   * Log a business event with automatic actor population
   */
  public async logEvent(params: {
    eventType: BusinessEventType;
    status: BusinessEventStatus;
    summary: string;
    entity?: BusinessEventEntity;
    correlationId?: string;
    durationMs?: number;
    meta?: Record<string, any>;
    error?: BusinessEventError;
    actor?: BusinessEventActor; // Optional override for actor
  }): Promise<BusinessEvent> {
    const actor = params.actor || await this.getCurrentActor();

    const event: BusinessEvent = {
      eventType: params.eventType,
      status: params.status,
      actor,
      summary: params.summary,
      entity: params.entity,
      correlationId: params.correlationId || this.generateCorrelationId(),
      durationMs: params.durationMs,
      meta: params.meta,
      error: params.error,
      dateCreated: Timestamp.now(),
      dateModified: Timestamp.now()
    };

    return this.addEvent(event);
  }

  /**
   * Add a business event to Firestore (basic CRUD - not using AbstractDatabaseService)
   */
  private addEvent(event: BusinessEvent): Promise<BusinessEvent> {
    // Remove undefined values from the event object since Firestore doesn't allow undefined
    const cleanedEvent = this.removeUndefinedFields(event);
    
    return new Promise<BusinessEvent>((resolve, reject) => {
      this.collection.add(cleanedEvent).then(ref => {
        const newItem = {
          id: ref.id,
          ...(cleanedEvent as any)
        };
        resolve(newItem);
      }).catch(error => {
        reject(error);
      });
    });
  }

  /**
   * Recursively remove undefined fields from an object (Firestore doesn't allow undefined values)
   */
  private removeUndefinedFields(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefinedFields(item));
    }
    
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key];
          if (value !== undefined) {
            cleaned[key] = this.removeUndefinedFields(value);
          }
        }
      }
      return cleaned;
    }
    
    return obj;
  }

  /**
   * Log a successful business object creation
   */
  public async logCreateSuccess(
    entityType: string,
    entityId: string,
    summary: string,
    meta?: Record<string, any>,
  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'BusinessObjectCreateSuccess',
      status: 'success',
      summary,
      entity: { type: entityType, id: entityId },
      meta
    });
  }

  /**
   * Log a failed business object creation
   */
  public async logCreateFailure(
    entityType: string,
    summary: string,
    error: BusinessEventError,
    meta?: Record<string, any>,
  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'BusinessObjectCreateFailed',
      status: 'failure',
      summary,
      entity: { type: entityType },
      error,
      meta
    });
  }

  /**
   * Log a business object creation attempt
   */
  public async logCreateAttempt(
    entityType: string,
    summary: string,
    meta?: Record<string, any>,
  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'BusinessObjectCreateAttempt',
      status: 'attempt',
      summary,
      entity: { type: entityType },
      meta
    });
  }

  /**
   * Log a successful business object update
   */
  public async logUpdateSuccess(
    entityType: string,
    entityId: string,
    summary: string,
    meta?: Record<string, any>,
  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'BusinessObjectUpdateSuccess',
      status: 'success',
      summary,
      entity: { type: entityType, id: entityId },
      meta
    });
  }

  /**
   * Log a failed business object update
   */
  public async logUpdateFailure(
    entityType: string,
    entityId: string,
    summary: string,
    error: BusinessEventError,
    meta?: Record<string, any>,
  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'BusinessObjectUpdateFailed',
      status: 'failure',
      summary,
      entity: { type: entityType, id: entityId },
      error,
      meta
    });
  }

  /**
   * Log a business object update attempt
   */
  public async logUpdateAttempt(
    entityType: string,
    entityId: string,
    summary: string,
    meta?: Record<string, any>,
  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'BusinessObjectUpdateAttempt',
      status: 'attempt',
      summary,
      entity: { type: entityType, id: entityId },
      meta
    });
  }

  /**
   * Log a successful business object deletion
   */
  public async logDeleteSuccess(
    entityType: string,
    entityId: string,
    summary: string,
    meta?: Record<string, any>,
  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'BusinessObjectDeleteSuccess',
      status: 'success',
      summary,
      entity: { type: entityType, id: entityId },
      meta
    });
  }

  /**
   * Log a failed business object deletion
   */
  public async logDeleteFailure(
    entityType: string,
    entityId: string,
    summary: string,
    error: BusinessEventError,
    meta?: Record<string, any>,

  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'BusinessObjectDeleteFailed',
      status: 'failure',
      summary,
      entity: { type: entityType, id: entityId },

      error,
      meta
    });
  }

  /**
   * Log a payment success
   */
  public async logPaymentSuccess(
    summary: string,
    meta?: Record<string, any>,

  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'PaymentSucceeded',
      status: 'success',
      summary,

      meta
    });
  }

  /**
   * Log a payment failure
   */
  public async logPaymentFailure(
    summary: string,
    error: BusinessEventError,
    meta?: Record<string, any>,

  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'PaymentFailed',
      status: 'failure',
      summary,

      error,
      meta
    });
  }

  /**
   * Log an email sent
   */
  public async logEmailSent(
    summary: string,
    meta?: Record<string, any>,

  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'EmailSent',
      status: 'success',
      summary,

      meta
    });
  }

  /**
   * Log an email failure
   */
  public async logEmailFailed(
    summary: string,
    error: BusinessEventError,
    meta?: Record<string, any>,

  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'EmailFailed',
      status: 'failure',
      summary,

      error,
      meta
    });
  }

  /**
   * Log a system error
   */
  public async logSystemError(
    summary: string,
    error: BusinessEventError,
    meta?: Record<string, any>,

  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'SystemError',
      status: 'failure',
      summary,

      error,
      meta
    });
  }

  /**
   * Log a user action
   */
  public async logUserAction(
    summary: string,
    meta?: Record<string, any>,

  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'UserAction',
      status: 'success',
      summary,

      meta
    });
  }

  /**
   * Log a custom event
   */
  public async logCustom(
    summary: string,
    status: BusinessEventStatus,
    meta?: Record<string, any>,
    entity?: BusinessEventEntity,
    error?: BusinessEventError
  ): Promise<BusinessEvent> {
    return this.logEvent({
      eventType: 'Custom',
      status,
      summary,
      entity,
      meta,
      error
    });
  }

  /**
   * Get events by correlation ID (useful for tracking related events)
   */
  public getEventsByCorrelationId(correlationId: string): Observable<BusinessEvent[]> {
    return this.afs.collection<BusinessEvent>('events', ref =>
      ref.where('correlationId', '==', correlationId)
        .orderBy('dateCreated', 'desc')
    ).snapshotChanges().pipe(
      map(changes => {
        return changes.map(a => {
          const data = a.payload.doc.data() as BusinessEvent;
          data.id = a.payload.doc.id;
          return data;
        });
      })
    );
  }

  /**
   * Get events by user ID
   */
  public getEventsByUserId(userId: string, limit?: number): Observable<BusinessEvent[]> {
    return this.afs.collection<BusinessEvent>('events', ref => {
      let query = ref.where('actor.userId', '==', userId)
        .orderBy('dateCreated', 'desc');
      if (limit) {
        query = query.limit(limit);
      }
      return query;
    }).snapshotChanges().pipe(
      map(changes => {
        return changes.map(a => {
          const data = a.payload.doc.data() as BusinessEvent;
          data.id = a.payload.doc.id;
          return data;
        });
      })
    );
  }

  /**
   * Get events by entity
   */
  public getEventsByEntity(entityType: string, entityId?: string, limit?: number): Observable<BusinessEvent[]> {
    return this.afs.collection<BusinessEvent>('events', ref => {
      let query: Query = ref.where('entity.type', '==', entityType);
      if (entityId) {
        query = query.where('entity.id', '==', entityId);
      }
      query = query.orderBy('dateCreated', 'desc');
      if (limit) {
        query = query.limit(limit);
      }
      return query;
    }).snapshotChanges().pipe(
      map(changes => {
        return changes.map(a => {
          const data = a.payload.doc.data() as BusinessEvent;
          data.id = a.payload.doc.id;
          return data;
        });
      })
    );
  }

  /**
   * Get events by event type
   */
  public getEventsByType(eventType: BusinessEventType, limit?: number): Observable<BusinessEvent[]> {
    return this.afs.collection<BusinessEvent>('events', ref => {
      let query = ref.where('eventType', '==', eventType)
        .orderBy('dateCreated', 'desc');
      if (limit) {
        query = query.limit(limit);
      }
      return query;
    }).snapshotChanges().pipe(
      map(changes => {
        return changes.map(a => {
          const data = a.payload.doc.data() as BusinessEvent;
          data.id = a.payload.doc.id;
          return data;
        });
      })
    );
  }

  /**
   * Get events by tenant ID
   */
  public getEventsByTenantId(tenantId: string, limit?: number): Observable<BusinessEvent[]> {
    return this.afs.collection<BusinessEvent>('events', ref => {
      let query = ref.where('tenantId', '==', tenantId)
        .orderBy('dateCreated', 'desc');
      if (limit) {
        query = query.limit(limit);
      }
      return query;
    }).snapshotChanges().pipe(
      map(changes => {
        return changes.map(a => {
          const data = a.payload.doc.data() as BusinessEvent;
          data.id = a.payload.doc.id;
          return data;
        });
      })
    );
  }

  /**
   * Get failed events (useful for debugging)
   */
  public getFailedEvents(limit?: number): Observable<BusinessEvent[]> {
    return this.afs.collection<BusinessEvent>('events', ref => {
      let query = ref.where('status', '==', 'failure')
        .orderBy('dateCreated', 'desc');
      if (limit) {
        query = query.limit(limit);
      }
      return query;
    }).snapshotChanges().pipe(
      map(changes => {
        return changes.map(a => {
          const data = a.payload.doc.data() as BusinessEvent;
          data.id = a.payload.doc.id;
          return data;
        });
      })
    );
  }

  /**
   * Get the current actor information from the authenticated user
   */
  private async getCurrentActor(): Promise<BusinessEventActor> {
    try {
      const firebaseUser = await this.afAuth.currentUser;

      // Lazily get AuthService to avoid circular dependency
      let user = null;
      if (this.injector) {
        try {
          const authService = this.injector.get(AuthService, null);
          if (authService) {
            user = await firstValueFrom(authService.$user.pipe(take(1)));
          }
        } catch (error) {
          // AuthService might not be available yet, continue without it
        }
      }

      if (firebaseUser && user) {
        return {
          userId: firebaseUser.uid,
          role: user.accountType || 'user',
          source: 'web'
        };
      } else if (firebaseUser) {
        return {
          userId: firebaseUser.uid,
          role: 'user',
          source: 'web'
        };
      } else {
        return {
          source: 'web'
        };
      }
    } catch (error) {
      // If we can't get the user, still log the event with minimal actor info
      return {
        source: 'web'
      };
    }
  }

  /**
   * Generate a correlation ID for tracking related events
   */
  private generateCorrelationId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

